import { mockCustomers } from '../../components/customers/data/mockCustomers';
import { mockGroups } from '../../components/customers/data/mockGroups';
import { mockSegments } from '../../components/customers/data/mockSegments';
import type { Customer, CustomerGroup } from '../../components/customers/data/types';
import type { CustomerAction } from './customerActions';
import * as T from './customerTypes';
import type { CustomersState } from './customerTypes';

export const initialCustomersState: CustomersState = {
  customers: mockCustomers,
  groups: mockGroups,
  segments: mockSegments,
  toasts: [],
};

const nowIso = () => new Date().toISOString();

/** Sync each customer's groupIds against the authoritative group memberships. */
function syncGroupIds(customers: Customer[], groups: CustomerGroup[]): Customer[] {
  const byCustomer = new Map<string, string[]>();
  groups.forEach((g) => g.memberIds.forEach((cid) => {
    const arr = byCustomer.get(cid) ?? [];
    arr.push(g.id);
    byCustomer.set(cid, arr);
  }));
  return customers.map((c) => {
    const next = byCustomer.get(c.id) ?? [];
    const same = next.length === c.groupIds.length && next.every((id) => c.groupIds.includes(id));
    return same ? c : { ...c, groupIds: next };
  });
}

export default function customerReducer(
  state: CustomersState = initialCustomersState,
  action: CustomerAction,
): CustomersState {
  switch (action.type) {
    case T.CUSTOMERS_ADD:
      return { ...state, customers: [action.payload, ...state.customers] };

    case T.CUSTOMERS_UPDATE:
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === action.payload.id ? { ...c, ...action.payload.updates, updatedAt: nowIso() } : c,
        ),
      };

    case T.CUSTOMERS_REMOVE: {
      const id = action.payload;
      const groups = state.groups.map((g) => ({ ...g, memberIds: g.memberIds.filter((m) => m !== id) }));
      return { ...state, customers: state.customers.filter((c) => c.id !== id), groups };
    }

    case T.CUSTOMERS_SET_FLAG: {
      const { id, flag, newStatus, auditEntry } = action.payload;
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === id ? { ...c, riskStatus: newStatus, activeFlag: flag, auditLog: [...c.auditLog, auditEntry], updatedAt: nowIso() } : c,
        ),
      };
    }

    case T.CUSTOMERS_CLEAR_FLAG: {
      const { id, auditEntry } = action.payload;
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === id ? { ...c, riskStatus: 'none', activeFlag: undefined, auditLog: [...c.auditLog, auditEntry], updatedAt: nowIso() } : c,
        ),
      };
    }

    case T.CUSTOMERS_RECORD_OPT_OUT: {
      const { id, record } = action.payload;
      // Consent records are only ever added.
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === id ? { ...c, marketingConsent: [...c.marketingConsent, record], updatedAt: nowIso() } : c,
        ),
      };
    }

    case T.CUSTOMERS_BULK_TAG: {
      const { ids, tags } = action.payload;
      const idSet = new Set(ids);
      return {
        ...state,
        customers: state.customers.map((c) =>
          idSet.has(c.id) ? { ...c, tags: Array.from(new Set([...c.tags, ...tags])), updatedAt: nowIso() } : c,
        ),
      };
    }

    case T.CUSTOMERS_SET_LIFECYCLE: {
      const { ids, state: lifecycle } = action.payload;
      const idSet = new Set(ids);
      return {
        ...state,
        customers: state.customers.map((c) =>
          idSet.has(c.id) ? { ...c, lifecycleState: lifecycle, updatedAt: nowIso() } : c,
        ),
      };
    }

    case T.CUSTOMERS_MERGE: {
      const { keepId, merged, removeIds } = action.payload;
      const removeSet = new Set(removeIds);
      // Re-point group memberships from removed records to the kept record.
      const groups = state.groups.map((g) => {
        if (!g.memberIds.some((m) => removeSet.has(m) || m === keepId)) return g;
        const memberIds = Array.from(new Set(g.memberIds.map((m) => (removeSet.has(m) ? keepId : m))));
        return { ...g, memberIds };
      });
      const keepGroupIds = groups.filter((g) => g.memberIds.includes(keepId)).map((g) => g.id);
      const customers = state.customers
        .filter((c) => !removeSet.has(c.id))
        .map((c) => (c.id === keepId ? { ...merged, groupIds: keepGroupIds, updatedAt: nowIso() } : c));
      return { ...state, customers, groups };
    }

    case T.CUSTOMERS_IMPORT:
      return { ...state, customers: [...action.payload, ...state.customers] };

    // ─── Groups ───
    case T.GROUPS_ADD: {
      const groups = [...state.groups, action.payload];
      return { ...state, groups, customers: syncGroupIds(state.customers, groups) };
    }

    case T.GROUPS_UPDATE:
      return {
        ...state,
        groups: state.groups.map((g) =>
          g.id === action.payload.id ? { ...g, ...action.payload.updates, updatedAt: nowIso() } : g,
        ),
      };

    case T.GROUPS_REMOVE: {
      const groups = state.groups.filter((g) => g.id !== action.payload);
      return { ...state, groups, customers: syncGroupIds(state.customers, groups) };
    }

    case T.GROUPS_SET_MEMBERS: {
      const { groupId, memberIds } = action.payload;
      const groups = state.groups.map((g) =>
        g.id === groupId ? { ...g, memberIds: Array.from(new Set(memberIds)), updatedAt: nowIso() } : g,
      );
      return { ...state, groups, customers: syncGroupIds(state.customers, groups) };
    }

    case T.GROUPS_BULK_ADD: {
      const { groupId, ids } = action.payload;
      const groups = state.groups.map((g) =>
        g.id === groupId ? { ...g, memberIds: Array.from(new Set([...g.memberIds, ...ids])), updatedAt: nowIso() } : g,
      );
      return { ...state, groups, customers: syncGroupIds(state.customers, groups) };
    }

    // ─── Segments ───
    case T.SEGMENTS_ADD:
      return { ...state, segments: [...state.segments, action.payload] };

    case T.SEGMENTS_UPDATE:
      return {
        ...state,
        segments: state.segments.map((s) =>
          s.id === action.payload.id ? { ...s, ...action.payload.updates, updatedAt: nowIso() } : s,
        ),
      };

    case T.SEGMENTS_REMOVE:
      return { ...state, segments: state.segments.filter((s) => s.id !== action.payload) };

    // ─── Saved cards (P2-1) ───
    case T.CARD_ADD: {
      const { customerId, card } = action.payload;
      return {
        ...state,
        customers: state.customers.map((c) => {
          if (c.id !== customerId) return c;
          const makeDefault = card.isDefault || c.savedCards.length === 0;
          const cleared = makeDefault ? c.savedCards.map((k) => ({ ...k, isDefault: false })) : c.savedCards;
          return { ...c, savedCards: [...cleared, { ...card, isDefault: makeDefault }], updatedAt: nowIso() };
        }),
      };
    }

    case T.CARD_REMOVE: {
      const { customerId, cardId } = action.payload;
      return {
        ...state,
        customers: state.customers.map((c) => {
          if (c.id !== customerId) return c;
          const remaining = c.savedCards.filter((k) => k.id !== cardId);
          // If the default was removed, promote the first remaining card.
          if (remaining.length > 0 && !remaining.some((k) => k.isDefault)) remaining[0] = { ...remaining[0], isDefault: true };
          return { ...c, savedCards: remaining, updatedAt: nowIso() };
        }),
      };
    }

    case T.CARD_SET_DEFAULT: {
      const { customerId, cardId } = action.payload;
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === customerId
            ? { ...c, savedCards: c.savedCards.map((k) => ({ ...k, isDefault: k.id === cardId })), updatedAt: nowIso() }
            : c,
        ),
      };
    }

    case T.CUSTOMERS_TOAST_ADD:
      return { ...state, toasts: [...state.toasts, action.payload] };

    case T.CUSTOMERS_TOAST_REMOVE:
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.payload) };

    default:
      return state;
  }
}
